import { ErrorMessageWrapper } from "@/common/components/ErrorMessageWrapper";
import ListFilters from "@/common/components/ListFilters";
import Table from "@/common/components/Table";
import TextField from "@/common/components/TextField";
import useDebounce from "@/common/hooks/useDebounce";
import { useFilterParams } from "@/common/hooks/useFilterParams";
import { Mapper } from "@/common/services/mapper";
import { CompanyBranchData } from "@/common/types/companyBranches.type";
import { SearchIcon, WrenchIcon } from "lucide-react";
import SelectEquipmentModal from "./components/SelectEquipmentModal";
import { useAvailableCompanyBranches } from "@/features/companyBranches/hooks/useAvailableCompanyBranches";
import { useEquipments } from "@/features/equipments/hooks/useEquipments";
import { EquipmentsTransformer } from "@/features/equipments/services/equipmentTransformer";
import { useMaintenanceWorkflows } from "../Create/hooks/useMaintenanceWorkflows";
import { MaintenanceWorkflowsTransformer } from "../../services/maintenanceWorkflowsTransformer";
import { Option } from "@/common/types/option";
import { maintenanceWorkflowsTableColumns } from "../../constants/maintenanceWorkflowsTableColumns.constant";
import ListHeader from "@/common/components/ListHeader";
import { maintenanceWorkflowFowardingStatusOptions } from "../../constants/maintenanceWorkflowsFowardingStatus.constant";

const MaintenanceWorkflowsList = () => {
  const { filterParams, setFilterParams } = useFilterParams();

  const [search, setSearch] = useDebounce(filterParams.search);
  const [searchEquipment, setSearchEquipment] = useDebounce('');
  const [searchBranch, setSearchBranch] = useDebounce('');

  const {
    data: companyBranchOptions,
  } = useAvailableCompanyBranches({
    filters: {
      search: searchBranch
    },
    mapper: Mapper.mapToOptions<CompanyBranchData>({
      labelFieldName: 'display_name',
      valueFieldName: 'id',
    }),
  });

  const {
    data: equipmentsOptions,
  } = useEquipments({
    filters: {
      search: searchEquipment,
    },
    mapper: Mapper.mapWithTransformer({
      transformer: EquipmentsTransformer.optionsTransformer(),
    }),
  });

  const {
    data: workflows,
    meta: workflowsMeta,
    isPending: workflowsIsPending,
    refetch: refetchWorkflows,
    error: workflowsError = {} as any,
  } = useMaintenanceWorkflows({
    filters: {
      search,
      page: Number(filterParams.page) || 1,
      branchId: filterParams.branch?.value,
      equipmentId: filterParams.equipment?.value,
      currentForwardingStatus: filterParams.currentForwardingStatus?.value
    },
    mapper: Mapper.mapWithTransformer({
      transformer: MaintenanceWorkflowsTransformer.tableTransformer(),
    }),
  });

  return (
    <div className="flex flex-col gap-6">
      <ListHeader
        Icon={<WrenchIcon className="w-10 h-10" strokeWidth={1} />}
        title="Processos de manutenção"
        actions={(
          <SelectEquipmentModal />
        )}
      />

      <ErrorMessageWrapper
        className=""
        statusCode={workflowsError?.status}
        refetchFunction={refetchWorkflows}
      >
        <div className="flex flex-col gap-6">
          <div className="flex flex-col gap-4">
            <TextField
              name="search"
              LeftIcon={<SearchIcon />}
              placeholder="Pesquise pela descrição"
              className="rounded-sm shadow-[4px_0px_16px_0px_rgba(0,_0,_0,_0.5)]"
              onChange={(e) => {
                setSearch((e.target as HTMLInputElement).value);
                setFilterParams((prev) => ({ ...prev, search: (e.target as HTMLInputElement).value }));
              }}
              value={filterParams.search}
            />

            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <ListFilters
                  filters={[
                    {
                      className: '',
                      onInputChange: setSearchEquipment,
                      onChange: (e) => setFilterParams((prev) => ({ ...prev, equipment: e })),
                      value: filterParams.equipment,
                      options: [{ label: 'Todas', value: '' }, ...(equipmentsOptions as Option[] || [])],
                      placeholder: 'Equipamento',
                    },
                    {
                      className: '',
                      onInputChange: setSearchBranch,
                      onChange: (e) => setFilterParams((prev) => ({ ...prev, branch: e })),
                      value: filterParams.branch,
                      options: [{ label: 'Todas', value: '' }, ...(companyBranchOptions || [])],
                      placeholder: 'Filial',
                    },
                    {
                      className: '',
                      onChange: (e) => setFilterParams((prev) => ({ ...prev, currentForwardingStatus: e })),
                      value: filterParams.currentForwardingStatus,
                      options: [{ label: 'Todos', value: '' }, ...(maintenanceWorkflowFowardingStatusOptions || [])],
                      placeholder: 'Status do Encaminhamento',
                    }
                  ]}
                />
              </div>
            </div>
          </div>

          <Table
            className="max-h-[calc(100vh-250px)]"
            columns={maintenanceWorkflowsTableColumns}
            isLoadingData={workflowsIsPending}
            page={filterParams.page || 1}
            rows={workflows}
            totalPages={workflowsMeta.total_pages}
            totalCount={workflowsMeta.total}
            hasPagination
            setPage={(page) => setFilterParams((prev) => ({ ...prev, page }))}
            rowEmptyValue="--"
          />
        </div>
      </ErrorMessageWrapper>
    </div>
  )
}

export default MaintenanceWorkflowsList;
